import { AttentionToday } from '@/components/admin/AttentionToday';
import { BackupDownload } from '@/components/admin/BackupDownload';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import apiClient, { adminAPI, getAuthToken } from '@/utils/api';
import { openDocument, downloadDocument } from '@/utils/documents';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft, Users, Building2, Briefcase, TrendingUp, Download, CalendarDays,
  RefreshCw, Search, ShieldAlert, ChevronLeft, ChevronRight, Trash2, X, AlertTriangle, FileText, ShieldCheck, Cpu, Smartphone, Video, Languages, RotateCcw, CheckCircle2,
  Bell, Clock, CreditCard, AlertCircle, MessageCircle, ArrowRight, TrendingDown, DollarSign
} from 'lucide-react';
import api from '@/utils/api';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, AreaChart, Area
} from 'recharts';

const PERIODS = [
  { key: '7', i18nKey: 'last7', days: 7 },
  { key: '30', i18nKey: 'last30', days: 30 },
  { key: '90', i18nKey: 'last90', days: 90 },
  { key: '365', i18nKey: 'lastYear', days: 365 },
];

const API = process.env.REACT_APP_BACKEND_URL;

// Authenticated file download — the backend requires a Bearer token, so a plain
// <a href> click (no Authorization header) returns 401 and the browser saves the
// error body as a bogus file. Fetch as a blob with the token, then trigger the save.
const downloadAuthedFile = async (url, fallbackFilename, onError) => {
  try {
    const res = await apiClient.get(url.replace(API, '').replace(/^\/api/, ''), {
      responseType: 'blob',
    });
    const disposition = res.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/i);
    const filename = match ? match[1] : fallbackFilename;
    const blobUrl = window.URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (e) {
    let msg = 'Não foi possível gerar o ficheiro. Tenta novamente.';
    try {
      if (e?.response?.data instanceof Blob) {
        const txt = await e.response.data.text();
        const parsed = JSON.parse(txt);
        if (parsed?.detail) msg = parsed.detail;
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      }
    } catch (_) {}
    if (onError) onError(msg); else alert(msg);
  }
};

const ROLE_TABS = [
  { value: '', i18nKey: 'all' },
  { value: 'candidate', i18nKey: 'candidates' },
  { value: 'employer', i18nKey: 'employers' },
];

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('pt-PT'); } catch { return '—'; }
};

const formatDateShort = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

/* ── Botão de upload do vídeo "Como se candidatar" ── */
function HowtoVideoUploadBtn() {
  const [uploading, setUploading] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const ref = React.useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/admin/howto-video/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
      toast.success('Vídeo "Como se candidatar" actualizado.');
    } catch {
      toast.error('Erro ao carregar o vídeo. Tente novamente.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input ref={ref} type="file" accept="video/mp4,video/*" className="hidden" onChange={handleFile} data-testid="howto-video-file-input" />
      <Button
        variant="outline"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        data-testid="nav-admin-howto-video"
        className="bg-teal-50 border-teal-200 hover:bg-teal-100 text-teal-700"
      >
        <Video className="h-4 w-4 mr-2" />
        {uploading ? 'A carregar…' : done ? 'Vídeo actualizado' : 'Vídeo Como Candidatar'}
      </Button>
    </>
  );
}

/* ── Modal de Migração de Títulos EN→PT ── */
function MigrationModal({ onClose }) {
  const [step, setStep] = useState('preview'); // 'preview' | 'confirm' | 'running' | 'done' | 'rollback_confirm' | 'rolling_back' | 'rolled_back'
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [rollbackResult, setRollbackResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/maintenance/jobs-migration-preview');
        setPreview(data);
      } catch (e) {
        toast.error('Erro ao carregar preview. Tenta de novo.');
        onClose();
      } finally {
        setPreviewLoading(false);
      }
    })();
  }, [onClose]);

  const execute = async () => {
    setStep('running');
    try {
      const { data } = await api.post('/admin/maintenance/fix-jobs-pt');
      setResult(data);
      setStep('done');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar migração.');
      setStep('preview');
    }
  };

  const rollback = async () => {
    setStep('rolling_back');
    try {
      const { data } = await api.post('/admin/maintenance/rollback-jobs-pt');
      setRollbackResult(data);
      setStep('rolled_back');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao reverter migração.');
      setStep('done');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="migration-modal">
      <Card className="max-w-lg w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700" aria-label="Fechar">
          <X className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
            <Languages className="h-5 w-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Migração de Títulos EN→PT</h3>
            <p className="text-xs text-gray-500">Corrige títulos em inglês nas vagas da base de dados</p>
          </div>
        </div>

        {previewLoading && (
          <div className="text-center py-10 text-gray-500">
            <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin opacity-40" />
            <p className="text-sm">A verificar vagas…</p>
          </div>
        )}

        {!previewLoading && step === 'preview' && preview && (
          <>
            {preview.total_affected === 0 ? (
              <div className="text-center py-6">
                <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-emerald-500" />
                <p className="font-semibold text-gray-700">Nenhuma vaga com títulos em inglês encontrada.</p>
                <p className="text-sm text-gray-500 mt-1">A base de dados já está actualizada.</p>
                {preview.has_backup && (
                  <div className="mt-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
                    <p className="font-semibold mb-1">Existe um backup de {preview.backup_count} vaga(s).</p>
                    <Button size="sm" variant="outline" className="border-amber-400 text-amber-700 mt-1" onClick={() => setStep('rollback_confirm')} data-testid="migration-rollback-btn">
                      <RotateCcw className="h-4 w-4 mr-1" /> Reverter migração anterior
                    </Button>
                  </div>
                )}
                <Button className="w-full mt-4" variant="outline" onClick={onClose}>Fechar</Button>
              </div>
            ) : (
              <>
                <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-100 text-sm text-blue-800">
                  <strong>{preview.total_affected} vaga(s)</strong> serão actualizadas.
                  {preview.has_backup && <span className="ml-2 text-blue-600">(backup anterior disponível para reverter)</span>}
                </div>

                <div className="space-y-2 mb-5">
                  <p className="text-xs font-semibold uppercase text-gray-400 tracking-wide mb-2">Alterações previstas:</p>
                  {preview.changes.map((ch, i) => (
                    <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                      <div className="flex-1 min-w-0">
                        <div className="text-red-600 font-medium truncate">«{ch.from}»</div>
                        <div className="text-xs text-gray-400 my-0.5">↓</div>
                        <div className="text-emerald-700 font-semibold truncate">«{ch.to}»</div>
                      </div>
                      <span className="shrink-0 text-xs text-gray-500 bg-white border border-gray-200 rounded-full px-2 py-0.5">{ch.count} vaga(s)</span>
                    </div>
                  ))}
                </div>

                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 mb-4">
                  <AlertTriangle className="h-3.5 w-3.5 inline mr-1" />
                  Os títulos originais serão guardados automaticamente para permitir reverter.
                </div>

                <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold" onClick={() => setStep('confirm')} data-testid="migration-proceed-btn">
                  Continuar para confirmação
                </Button>
              </>
            )}
          </>
        )}

        {step === 'confirm' && preview && (
          <>
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 mb-5">
              <p className="font-bold mb-1">Confirmar execução da migração?</p>
              <p>Esta acção irá actualizar <strong>{preview.total_affected} vaga(s)</strong> na base de dados.</p>
              <p className="mt-1 text-xs">Um backup será guardado automaticamente para permitir reverter.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('preview')}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={execute} data-testid="migration-confirm-btn">
                Executar Migração
              </Button>
            </div>
          </>
        )}

        {step === 'running' && (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-amber-500" />
            <p className="text-gray-600 font-medium">A executar migração…</p>
          </div>
        )}

        {step === 'done' && result && (
          <>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4">
              <CheckCircle2 className="h-4 w-4 inline mr-1" />
              <strong>Migração concluída.</strong> {result.summary}
              {result.backup_saved > 0 && <div className="mt-1 text-xs text-emerald-600">Backup de {result.backup_saved} título(s) guardado.</div>}
            </div>

            {result.titles_fixed?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Títulos corrigidos:</p>
                {result.titles_fixed.map((s, i) => <div key={i} className="text-xs text-gray-600 py-0.5">{s}</div>)}
              </div>
            )}
            {result.categories_fixed?.length > 0 && (
              <div className="mb-3">
                <p className="text-xs font-semibold uppercase text-gray-400 mb-1">Categorias corrigidas:</p>
                {result.categories_fixed.map((s, i) => <div key={i} className="text-xs text-gray-600 py-0.5">{s}</div>)}
              </div>
            )}

            <Button variant="outline" className="w-full mb-2 border-red-200 text-red-700 hover:bg-red-50" onClick={() => setStep('rollback_confirm')} data-testid="migration-rollback-btn">
              <RotateCcw className="h-4 w-4 mr-2" /> Reverter para títulos originais
            </Button>
            <Button className="w-full" onClick={onClose} data-testid="migration-close-btn">Fechar</Button>
          </>
        )}

        {step === 'rollback_confirm' && (
          <>
            <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800 mb-5">
              <p className="font-bold mb-1">Reverter migração?</p>
              <p>Os títulos serão restaurados para os valores originais guardados no backup.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep(result ? 'done' : 'preview')}>Cancelar</Button>
              <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={rollback} data-testid="migration-rollback-confirm-btn">
                Reverter Agora
              </Button>
            </div>
          </>
        )}

        {step === 'rolling_back' && (
          <div className="text-center py-10">
            <RefreshCw className="h-8 w-8 mx-auto mb-3 animate-spin text-red-400" />
            <p className="text-gray-600">A reverter títulos…</p>
          </div>
        )}

        {step === 'rolled_back' && rollbackResult && (
          <>
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4">
              <CheckCircle2 className="h-4 w-4 inline mr-1" />
              <strong>Reversão concluída.</strong> {rollbackResult.summary}
            </div>
            <Button className="w-full" onClick={onClose} data-testid="migration-close-btn">Fechar</Button>
          </>
        )}
      </Card>
    </div>
  );
}

// ─── Helper: formatar CVE ────────────────────────────────────────────────────
function formatCVE(value) {
  if (!value && value !== 0) return '—';
  return value.toLocaleString('pt-PT') + ' CVE';
}

// ─── Bloco "Precisa de atenção hoje" ─────────────────────────────────────────
const ATTENTION_ITEMS = [
  {
    key: 'trials_no_jobs',
    label: 'Trials s/ vagas',
    icon: AlertCircle,
    path: '/admin/companies',
    urgentColor: 'bg-red-50 border-red-200 text-red-700',
    zeroColor: 'bg-gray-50 border-gray-200 text-gray-400',
  },
  {
    key: 'prospects_to_respond',
    label: 'Prospects a responder',
    icon: MessageCircle,
    path: '/admin/prospects',
    urgentColor: 'bg-orange-50 border-orange-200 text-orange-700',
    zeroColor: 'bg-gray-50 border-gray-200 text-gray-400',
  },
  {
    key: 'duplicate_nif',
    label: 'NIFs duplicados',
    icon: AlertTriangle,
    path: '/admin/companies',
    urgentColor: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    zeroColor: 'bg-gray-50 border-gray-200 text-gray-400',
  },
  {
    key: 'pending_payments',
    label: 'Pagamentos pendentes',
    icon: CreditCard,
    path: '/admin/purchases',
    urgentColor: 'bg-red-50 border-red-200 text-red-700',
    zeroColor: 'bg-gray-50 border-gray-200 text-gray-400',
  },
  {
    key: 'jobs_expiring_7d',
    label: 'Vagas a expirar (7 dias)',
    icon: Clock,
    path: '/admin/jobs',
    urgentColor: 'bg-amber-50 border-amber-200 text-amber-700',
    zeroColor: 'bg-gray-50 border-gray-200 text-gray-400',
  },
];

function AttentionBlock({ data, loading, onNavigate }) {
  const allClear = data && ATTENTION_ITEMS.every(it => (data[it.key] ?? 0) === 0);

  return (
    <div className="mb-6" data-testid="attention-block">
      <div className="flex items-center gap-2 mb-3">
        <Bell className={`h-4 w-4 ${allClear ? 'text-emerald-500' : 'text-amber-500'}`} />
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Outros alertas da plataforma</h2>
        {allClear && (
          <span className="text-xs text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Tudo em ordem</span>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {ATTENTION_ITEMS.map(item => {
          const count = data?.[item.key] ?? 0;
          const active = count > 0;
          const colorClass = active ? item.urgentColor : item.zeroColor;
          return (
            <button
              key={item.key}
              onClick={() => onNavigate(item.path)}
              className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border text-center transition-all hover:scale-105 hover:shadow-sm ${colorClass}`}
              data-testid={`attention-item-${item.key}`}
            >
              <item.icon className="h-4 w-4" />
              <span className="text-2xl font-bold leading-none">
                {loading ? '…' : count}
              </span>
              <span className="text-xs leading-tight">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Funil de Empresas ────────────────────────────────────────────────────────
const FUNNEL_STEPS = [
  { key: 'registered',           label: 'Registada',           icon: Building2, color: 'bg-blue-100 text-blue-700' },
  { key: 'published',            label: 'Publicou 1ª vaga',    icon: Briefcase, color: 'bg-indigo-100 text-indigo-700' },
  { key: 'received_applications',label: 'Recebeu candidaturas',icon: Users,     color: 'bg-violet-100 text-violet-700' },
  { key: 'bought_plan',          label: 'Comprou um plano',    icon: CreditCard, color: 'bg-emerald-100 text-emerald-700' },
];

const FUNNEL_PCTS = ['pct_to_published', 'pct_to_apps', 'pct_to_plan'];

function CompanyFunnel({ data, loading }) {
  return (
    <div className="mb-6" data-testid="company-funnel">
      <div className="flex items-center gap-2 mb-3">
        <TrendingDown className="h-4 w-4 text-indigo-500" />
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Funil de Empresas</h2>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-4 overflow-x-auto">
        <div className="flex items-center min-w-max gap-0">
          {FUNNEL_STEPS.map((step, i) => {
            const count = data?.[step.key] ?? 0;
            const pctKey = FUNNEL_PCTS[i - 1];
            const pctValue = i > 0 ? (data?.[pctKey] ?? 0) : null;

            return (
              <React.Fragment key={step.key}>
                {i > 0 && (
                  <div className="flex flex-col items-center mx-3" data-testid={`funnel-pct-${i}`}>
                    <ArrowRight className="h-4 w-4 text-gray-300" />
                    <span className={`text-[11px] font-semibold mt-0.5 ${pctValue >= 50 ? 'text-emerald-600' : pctValue >= 25 ? 'text-amber-600' : 'text-red-500'}`}>
                      {loading ? '…' : `${pctValue}%`}
                    </span>
                  </div>
                )}
                <div
                  className={`flex flex-col items-center gap-1.5 p-3 rounded-xl min-w-[100px] ${step.color}`}
                  data-testid={`funnel-step-${step.key}`}
                >
                  <step.icon className="h-4 w-4" />
                  <span className="text-xl font-bold leading-none">{loading ? '…' : count.toLocaleString('pt-PT')}</span>
                  <span className="text-[11px] text-center leading-tight">{step.label}</span>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Cartão de Receita (CVE) ──────────────────────────────────────────────────
function RevenueCard({ data }) {
  return (
    <div className="mb-6" data-testid="revenue-card">
      <div className="flex items-center gap-2 mb-3">
        <DollarSign className="h-4 w-4 text-emerald-500" />
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Receita</h2>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-emerald-100 rounded-xl p-4" data-testid="revenue-total">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Total acumulado</div>
          <div className="text-2xl font-bold text-emerald-700">{formatCVE(data?.total_cve ?? 0)}</div>
        </div>
        <div className="bg-white border border-blue-100 rounded-xl p-4" data-testid="revenue-month">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">Este mês</div>
          <div className="text-2xl font-bold text-blue-700">{formatCVE(data?.this_month_cve ?? 0)}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState([]);
  const [periodDays, setPeriodDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  // Users table
  const [roleTab, setRoleTab] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(25);
  const [usersData, setUsersData] = useState({ items: [], total: 0, pages: 0 });
  const [usersLoading, setUsersLoading] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null); // {id, role, name, email}
  const [verifying, setVerifying] = useState(null);
  const [pwaStats, setPwaStats] = useState(null);

  const toggleVerify = async (u) => {
    const next = !u.extra?.nif_verified;
    if (next && !window.confirm('Confirma que verificou o NIF desta empresa?')) return;
    setVerifying(u.id);
    try {
      await adminAPI.verifyEmployer(u.id, next);
      setUsersData((d) => ({ ...d, items: d.items.map((x) => (x.id === u.id ? { ...x, extra: { ...x.extra, nif_verified: next } } : x)) }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Erro ao verificar');
    } finally {
      setVerifying(null);
    }
  };
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);

  // Novos: atenção, funil, receita
  const [attention, setAttention] = useState(null);
  const [attentionLoading, setAttentionLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [revenue, setRevenue] = useState(null);

  // Carregar dados dos novos widgets
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/overview/attention');
        setAttention(data);
      } catch (e) {
        console.error('Erro ao carregar métricas de atenção', e);
      } finally {
        setAttentionLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/overview/funnel');
        setFunnel(data);
      } catch (e) {
        console.error('Erro ao carregar funil', e);
      } finally {
        setFunnelLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/admin/overview/revenue');
        setRevenue(data);
      } catch (e) {
        console.error('Erro ao carregar receita', e);
      }
    })();
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') navigate('/dashboard');
  }, [user, navigate]);

  const loadStats = useCallback(async () => {
    setError('');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const [ov, ts, pwa] = await Promise.all([
        adminAPI.analyticsOverview({ signal: controller.signal }),
        adminAPI.analyticsTimeseries(periodDays, { signal: controller.signal }),
        api.get('/admin/pwa/stats', { signal: controller.signal }).catch(() => ({ data: null })),
      ]);
      setOverview(ov.data);
      setTimeseries(ts.data.series || []);
      if (pwa.data) setPwaStats(pwa.data);
    } catch (e) {
      const msg = e?.code === 'ERR_CANCELED'
        ? 'O pedido demorou demasiado (>15s). Verifica a ligação ou tenta mais tarde.'
        : (e?.response?.data?.detail || t('adminDashboard.errorLoadStats'));
      setError(msg);
    } finally {
      clearTimeout(timeout);
    }
  }, [periodDays, t]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const params = {
        role: roleTab || undefined,
        search: search || undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        page,
        limit,
      };
      const res = await adminAPI.analyticsUsers(params);
      setUsersData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminDashboard.errorLoadUsers'));
    } finally {
      setUsersLoading(false);
    }
  }, [roleTab, search, dateFrom, dateTo, page, limit, t]);

  // Initial + refresh loop for overview
  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadStats();
      setLoading(false);
    })();
  }, [loadStats]);

  useEffect(() => {
    const t = setInterval(async () => {
      setRefreshing(true);
      await loadStats();
      setRefreshing(false);
    }, 30000);
    return () => clearInterval(t);
  }, [loadStats]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadStats(), loadUsers()]);
    setRefreshing(false);
  };

  const handleExport = () => {
    const url = adminAPI.analyticsUsersExportUrl({
      role: roleTab || undefined,
      search: search || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    });
    downloadAuthedFile(url, 'trabadju_utilizadores.csv', setError);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError('');
    try {
      const res = await adminAPI.deleteUser(deleteTarget.role, deleteTarget.id, deleteReason || undefined);
      setDeleteResult(res.data);
      // reload lists and overview
      await Promise.all([loadStats(), loadUsers()]);
    } catch (e) {
      setError(e?.response?.data?.detail || t('adminDashboard.errorDeleteUser'));
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteTarget(null);
    setDeleteReason('');
    setDeleteResult(null);
  };

  const resetPageAnd = (fn) => (e) => { setPage(1); fn(e.target.value); };

  const chartData = useMemo(
    () => timeseries.map((r) => ({ ...r, label: formatDateShort(r.date) })),
    [timeseries]
  );

  const totalInPeriod = useMemo(() => {
    return timeseries.reduce((acc, r) => acc + r.candidates + r.employers, 0);
  }, [timeseries]);

  if (user && user.role !== 'admin') return null;

  return (
    <div className="min-h-screen bg-gray-50 py-10" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-7 w-7 text-red-600" />
            <h1 className="text-3xl font-bold">{t('adminDashboard.title')}</h1>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="admin-refresh"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {t('adminDashboard.refresh')}
          </Button>
        </div>
        <p className="text-gray-600 mb-8">
          {t('adminDashboard.subtitle')}
        </p>

        <AttentionToday />
        <div className="mt-6"><BackupDownload /></div>

        {error && !overview ? (
          /* ── Erro de API: nunca mostrar zeros ── */
          <div className="flex flex-col items-center gap-4 py-20 text-center" data-testid="admin-stats-error">
            <div className="rounded-xl border border-red-200 bg-red-50 px-6 py-5 max-w-md">
              <p className="text-red-700 font-semibold mb-1">Não foi possível carregar os dados.</p>
              <p className="text-red-600 text-sm mb-4">{error}</p>
              <button
                onClick={() => { setError(null); loadStats(); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-navy/90 transition"
                data-testid="admin-stats-retry-btn"
              >
                <RefreshCw className="h-4 w-4" /> Tentar de novo
              </button>
            </div>
          </div>
        ) : error && overview ? (
          <div className="mb-4 p-3 rounded bg-amber-50 text-amber-700 text-sm border border-amber-200 flex items-center gap-2">
            <RefreshCw className="h-4 w-4 flex-none" />
            <span>Dados podem estar desactualizados. {error}</span>
            <button onClick={() => { setError(null); loadStats(); }} className="ml-auto underline">Actualizar</button>
          </div>
        ) : null}

        {loading ? (
          <div className="text-center py-16 text-gray-500">{t('adminDashboard.loadingStats')}</div>
        ) : !overview && !error ? null : overview ? (
          <>
            {/* ── Widgets de atenção / funil / receita ── */}
            <AttentionBlock data={attention} loading={attentionLoading} onNavigate={navigate} />
            <CompanyFunnel data={funnel} loading={funnelLoading} />
            {revenue !== null && <RevenueCard data={revenue} />}

            {/* Overview cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <MetricCard
                icon={Users}
                label={t('adminDashboard.metricCandidatesTotal')}
                value={overview?.candidates?.total ?? 0}
                sub={t('adminDashboard.metricToday', { count: overview?.candidates?.today ?? 0 })}
                color="emerald"
                testid="metric-candidates-total"
              />
              <MetricCard
                icon={Building2}
                label={t('adminDashboard.metricEmployersTotal')}
                value={overview?.employers?.total ?? 0}
                sub={t('adminDashboard.metricToday', { count: overview?.employers?.today ?? 0 })}
                color="blue"
                testid="metric-employers-total"
              />
              <MetricCard
                icon={Briefcase}
                label={t('adminDashboard.metricActiveJobs')}
                value={overview?.jobs?.active ?? 0}
                sub={t('adminDashboard.metricTotalJobs', { count: overview?.jobs?.total ?? 0 })}
                color="amber"
                testid="metric-jobs-active"
              />
              <MetricCard
                icon={TrendingUp}
                label={t('adminDashboard.metricApplications')}
                value={overview?.applications_total ?? 0}
                sub={t('adminDashboard.metricHistoricTotal')}
                color="violet"
                testid="metric-applications"
              />
            </div>

            {/* PWA Install stats */}
            {pwaStats !== null && (
              <Card className="p-4 mb-6 border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50" data-testid="pwa-install-card">
                <div className="flex items-center gap-2 mb-3">
                  <Smartphone className="h-5 w-5 text-indigo-600" />
                  <h2 className="font-semibold text-gray-800">Instalações da App</h2>
                  <span className="ml-auto text-2xl font-bold text-indigo-700" data-testid="pwa-total-installs">{pwaStats.total}</span>
                  <span className="text-xs text-gray-500">total</span>
                </div>
                <div className="flex gap-4 flex-wrap mb-3">
                  <div className="flex items-center gap-1.5 text-sm" data-testid="pwa-android-installs">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block"></span>
                    <span className="text-gray-600">Android</span>
                    <span className="font-bold text-emerald-700">{pwaStats.android}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm" data-testid="pwa-ios-installs">
                    <span className="w-2.5 h-2.5 rounded-full bg-gray-800 inline-block"></span>
                    <span className="text-gray-600">iOS</span>
                    <span className="font-bold text-gray-800">{pwaStats.ios}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm" data-testid="pwa-desktop-installs">
                    <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block"></span>
                    <span className="text-gray-600">Desktop</span>
                    <span className="font-bold text-blue-700">{pwaStats.desktop}</span>
                  </div>
                  {pwaStats.recent?.length > 0 && (
                    <div className="ml-auto text-xs text-gray-400 self-center">
                      Última: {pwaStats.recent[0]?.installed_at ? new Date(pwaStats.recent[0].installed_at).toLocaleString('pt-PT') : '—'}
                      {' · '}{pwaStats.recent[0]?.os}
                    </div>
                  )}
                </div>
                {pwaStats.daily?.length > 0 && (
                  <div style={{ width: '100%', height: 80 }}>
                    <ResponsiveContainer>
                      <AreaChart data={pwaStats.daily} margin={{ top: 2, right: 4, left: -30, bottom: 0 }}>
                        <defs>
                          <linearGradient id="pwaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.4} />
                            <stop offset="95%" stopColor="#6366f1" stopOpacity={0}   />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 9 }} allowDecimals={false} />
                        <Tooltip formatter={(v) => [v, 'instalações']} labelStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="installs" stroke="#6366f1" fill="url(#pwaGrad)" strokeWidth={2} dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card>
            )}

            {/* Time-window breakdown */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <BreakdownCard
                title={t('adminDashboard.roleTab.candidates')}
                data={overview?.candidates}
                icon={Users}
                color="emerald"
              />
              <BreakdownCard
                title={t('adminDashboard.roleTab.employers')}
                data={overview?.employers}
                icon={Building2}
                color="blue"
              />
            </div>

            {/* Evolution chart */}
            <Card className="p-5 mb-8" data-testid="registrations-chart">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold">{t('adminDashboard.chartTitle')}</h2>
                  <p className="text-xs text-gray-500">
                    {t('adminDashboard.chartSubtitle', { count: totalInPeriod })}
                  </p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {PERIODS.map((p) => (
                    <button
                      key={p.key}
                      onClick={() => setPeriodDays(p.days)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-all ${
                        periodDays === p.days
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                      data-testid={`period-${p.key}`}
                    >
                      {t(`adminDashboard.period.${p.i18nKey}`)}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ width: '100%', height: 300 }}>
                <ResponsiveContainer>
                  <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colCand" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colEmp" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                      labelFormatter={(l, p) => (p && p[0] ? p[0].payload.date : l)}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area
                      type="monotone"
                      dataKey="candidates"
                      name={t('adminDashboard.roleTab.candidates')}
                      stroke="#10b981"
                      fill="url(#colCand)"
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="employers"
                      name={t('adminDashboard.roleTab.employers')}
                      stroke="#3b82f6"
                      fill="url(#colEmp)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            {/* Users table */}
            <Card className="p-5" data-testid="users-table-card">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <h2 className="text-lg font-semibold">{t('adminDashboard.usersTableTitle')}</h2>
                <Button onClick={handleExport} variant="outline" data-testid="export-csv-btn">
                  <Download className="h-4 w-4 mr-2" />
                  {t('adminDashboard.exportCsv')}
                </Button>
              </div>

              {/* Role tabs */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {ROLE_TABS.map((r) => (
                  <button
                    key={r.value || 'all'}
                    onClick={() => { setPage(1); setRoleTab(r.value); }}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      roleTab === r.value
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                    }`}
                    data-testid={`role-tab-${r.value || 'all'}`}
                  >
                    {t(`adminDashboard.roleTab.${r.i18nKey}`)}
                  </button>
                ))}
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-4">
                <div className="relative md:col-span-2">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    className="pl-9"
                    placeholder={t('adminDashboard.searchPlaceholder')}
                    value={search}
                    onChange={resetPageAnd(setSearch)}
                    data-testid="user-search-input"
                  />
                </div>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={resetPageAnd(setDateFrom)}
                  data-testid="date-from-input"
                />
                <Input
                  type="date"
                  value={dateTo}
                  onChange={resetPageAnd(setDateTo)}
                  data-testid="date-to-input"
                />
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-gray-500 uppercase text-xs border-b">
                    <tr>
                      <th className="py-2 pr-3">{t('adminDashboard.colType')}</th>
                      <th className="py-2 pr-3">{t('adminDashboard.colName')}</th>
                      <th className="py-2 pr-3">{t('adminDashboard.colEmail')}</th>
                      <th className="py-2 pr-3">{t('adminDashboard.colPhone')}</th>
                      <th className="py-2 pr-3">{t('adminDashboard.colLocation')}</th>
                      <th className="py-2 pr-3">{t('adminDashboard.colDate')}</th>
                      <th className="py-2 pr-3 text-right">{t('adminDashboard.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersLoading ? (
                      <tr><td colSpan={7} className="py-6 text-center text-gray-500">{t('adminDashboard.loadingGeneric')}</td></tr>
                    ) : usersData.items.length === 0 ? (
                      <tr><td colSpan={7} className="py-6 text-center text-gray-500">{t('adminDashboard.noRecords')}</td></tr>
                    ) : usersData.items.map((u) => (
                      <tr key={`${u.role}-${u.id}`} className="border-b hover:bg-gray-50">
                        <td className="py-2 pr-3">
                          <Badge className={
                            u.role === 'candidate'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-blue-100 text-blue-800'
                          }>
                            {u.role === 'candidate' ? t('adminDashboard.roleCandidate') : t('adminDashboard.roleEmployer')}
                          </Badge>
                        </td>
                        <td className="py-2 pr-3 font-medium">
                          <div className="flex items-center gap-1.5">
                            {u.name || '—'}
                            {u.role === 'employer' && u.extra?.nif_verified && (
                              <ShieldCheck className="h-4 w-4 text-emerald-600" title={t('adminDashboard.verifiedCompany')} data-testid={`verified-icon-${u.id}`} />
                            )}
                          </div>
                          {u.role === 'employer' && <div className="text-[11px] text-gray-500 font-normal">NIF: {u.extra?.fiscal_nif || '—'}</div>}
                        </td>
                        <td className="py-2 pr-3 text-gray-700">{u.email}</td>
                        <td className="py-2 pr-3 text-gray-600">{u.phone || '—'}</td>
                        <td className="py-2 pr-3 text-gray-600">{u.location || '—'}</td>
                        <td className="py-2 pr-3 text-gray-500 whitespace-nowrap">{formatDate(u.created_at)}</td>
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          {u.role === 'employer' && (
                            <Button
                              size="sm"
                              variant={u.extra?.nif_verified ? 'outline' : 'ghost'}
                              className={u.extra?.nif_verified ? 'text-emerald-700 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 mr-1' : 'text-gray-600 hover:text-emerald-700 hover:bg-emerald-50 mr-1'}
                              onClick={() => toggleVerify(u)}
                              disabled={verifying === u.id}
                              data-testid={`verify-employer-${u.id}`}
                              title={u.extra?.nif_verified ? t('adminDashboard.unverifyTitle') : t('adminDashboard.verifyTitle')}
                            >
                              <ShieldCheck className="h-4 w-4 mr-1" />
                              {u.extra?.nif_verified ? t('adminDashboard.verified') : t('adminDashboard.verify')}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setDeleteTarget(u)}
                            data-testid={`delete-user-${u.id}`}
                            title={u.role === 'candidate' ? t('adminDashboard.deleteCandidateTitle') : t('adminDashboard.deleteEmployerTitle')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
                <p className="text-xs text-gray-500" data-testid="pagination-info">
                  {t('adminDashboard.paginationInfo', { total: usersData.total, page, pages: Math.max(1, usersData.pages) })}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= usersData.pages}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="pagination-next"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </>
        ) : null}

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            data-testid="delete-modal"
            onClick={(e) => e.target === e.currentTarget && !deleting && closeDeleteDialog()}
          >
            <Card className="max-w-md w-full p-6 relative">
              <button
                onClick={closeDeleteDialog}
                disabled={deleting}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
                aria-label={t('adminDashboard.close')}
              >
                <X className="h-5 w-5" />
              </button>

              {deleteResult ? (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-emerald-100 text-emerald-700">
                      <Trash2 className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-semibold">{t('adminDashboard.userDeletedTitle')}</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    <strong>{deleteResult.target_name || deleteResult.target_email}</strong> {t('adminDashboard.userDeletedBody')}
                  </p>
                  <div className="bg-gray-50 rounded-md p-3 text-xs text-gray-700 space-y-1 mb-4">
                    {typeof deleteResult.jobs_removed === 'number' && (
                      <div>{t('adminDashboard.jobsRemoved')}: <strong>{deleteResult.jobs_removed}</strong></div>
                    )}
                    {typeof deleteResult.applications_removed === 'number' && (
                      <div>{t('adminDashboard.applicationsRemoved')}: <strong>{deleteResult.applications_removed}</strong></div>
                    )}
                    {typeof deleteResult.favourites_removed === 'number' && (
                      <div>{t('adminDashboard.favouritesRemoved')}: <strong>{deleteResult.favourites_removed}</strong></div>
                    )}
                    {typeof deleteResult.cv_documents_removed === 'number' && (
                      <div>{t('adminDashboard.cvsRemoved')}: <strong>{deleteResult.cv_documents_removed}</strong></div>
                    )}
                    {typeof deleteResult.ad_packs_removed === 'number' && (
                      <div>{t('adminDashboard.adPacksRemoved')}: <strong>{deleteResult.ad_packs_removed}</strong></div>
                    )}
                    {typeof deleteResult.sponsorships_removed === 'number' && (
                      <div>{t('adminDashboard.sponsorshipsRemoved')}: <strong>{deleteResult.sponsorships_removed}</strong></div>
                    )}
                  </div>
                  <Button className="w-full" onClick={closeDeleteDialog} data-testid="delete-done">
                    {t('adminDashboard.close')}
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 rounded-full bg-red-100 text-red-700">
                      <AlertTriangle className="h-5 w-5" />
                    </div>
                    <h3 className="text-lg font-semibold">
                      {deleteTarget.role === 'candidate' ? t('adminDashboard.confirmDeleteCandidate') : t('adminDashboard.confirmDeleteEmployer')}
                    </h3>
                  </div>

                  <p className="text-sm text-gray-700 mb-3">
                    {t('adminDashboard.willDelete')} <strong>{deleteTarget.name || deleteTarget.email}</strong> ({deleteTarget.email}).
                  </p>

                  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-900 mb-4">
                    {deleteTarget.role === 'candidate' ? (
                      <>{t('adminDashboard.cascadeCandidate')}</>
                    ) : (
                      <>{t('adminDashboard.cascadeEmployer')}<br/>
                      <strong>{t('adminDashboard.cascadeEmployerKeep')}</strong></>
                    )}
                  </div>

                  <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
                    {t('adminDashboard.reasonLabel')}
                  </label>
                  <Input
                    value={deleteReason}
                    onChange={(e) => setDeleteReason(e.target.value)}
                    placeholder={t('adminDashboard.reasonPlaceholder')}
                    disabled={deleting}
                    data-testid="delete-reason-input"
                  />

                  <div className="flex gap-2 mt-5">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={closeDeleteDialog}
                      disabled={deleting}
                      data-testid="delete-cancel"
                    >
                      {t('adminDashboard.cancel')}
                    </Button>
                    <Button
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                      onClick={confirmDelete}
                      disabled={deleting}
                      data-testid="delete-confirm"
                    >
                      {deleting ? t('adminDashboard.deleting') : t('adminDashboard.confirmDelete')}
                    </Button>
                  </div>
                </>
              )}
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color, testid }) {
  const map = {
    emerald: 'bg-emerald-100 text-emerald-700',
    blue: 'bg-blue-100 text-blue-700',
    amber: 'bg-amber-100 text-amber-700',
    violet: 'bg-violet-100 text-violet-700',
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
          <div className="text-3xl font-bold mt-1">{Number(value).toLocaleString('pt-PT')}</div>
          <div className="text-xs text-gray-500 mt-1">{sub}</div>
        </div>
        <div className={`p-3 rounded-lg ${map[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function BreakdownCard({ title, data, icon: Icon, color }) {
  const { t } = useTranslation();
  const map = {
    emerald: 'text-emerald-700 bg-emerald-50',
    blue: 'text-blue-700 bg-blue-50',
  };
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className={`p-2 rounded-md ${map[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-4 gap-2 text-center">
        <MiniStat label={t('adminDashboard.miniTotal')} value={data?.total ?? 0} />
        <MiniStat label={t('adminDashboard.miniToday')} value={data?.today ?? 0} />
        <MiniStat label={t('adminDashboard.miniWeek')} value={data?.week ?? 0} />
        <MiniStat label={t('adminDashboard.miniMonth')} value={data?.month ?? 0} />
      </div>
    </Card>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="p-2 rounded-md bg-gray-50">
      <div className="text-lg font-semibold">{Number(value).toLocaleString('pt-PT')}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}
